import { StaticImageData } from 'next/image';
import { PmsName } from '@gql';
import Arrow from '../../public/assets/img/integrations/arrow.png';
import { routes } from '@routes';

// Import all partner logos
import Barefoot from '../../public/assets/img/integrations/barefoot.png';
import BlueTent from '../../public/assets/img/integrations/blue-tent.png';
import Bookster from '../../public/assets/img/integrations/bookster.png';
import Boost from '../../public/assets/img/integrations/boost.png';
import Ciirus from '../../public/assets/img/integrations/ciirus.png';
import Escapia from '../../public/assets/img/integrations/escapia.png';
import Guesty from '../../public/assets/img/integrations/guesty.png';
import Hospitable from '../../public/assets/img/integrations/hospitable-logo.png';
import HostAway from '../../public/assets/img/integrations/hostaway.png';
import Hostfully from '../../public/assets/img/integrations/hostfully-logo-long.png';
import LiveRez from '../../public/assets/img/integrations/live-rez.png';
import Lodgify from '../../public/assets/img/integrations/lodgify-logo-long.png';
import Lodgix from '../../public/assets/img/integrations/lodgix.png';
import OwnerRez from '../../public/assets/img/integrations/ownerrez-logo-long.png';
import QuickConnect from '../../public/assets/img/integrations/quick-connect.png';
import RedAwning from '../../public/assets/img/integrations/redawning-logo.png';
import RentalsUnited from '../../public/assets/img/integrations/rentals-united-square.png';
import Lmpm from '../../public/assets/img/integrations/lmpm.png';
import Streamline from '../../public/assets/img/integrations/streamline.png';
import Track from '../../public/assets/img/integrations/track.png';
import VacayHome from '../../public/assets/img/integrations/vacay-home.png';
import Vrm from '../../public/assets/img/integrations/vrm.png';

// SVG versions for the list page
import BarefootSvg from '../../public/assets/img/integrations/barefoot-logo-primary.svg';
import BlueTentSvg from '../../public/assets/img/integrations/blue-tent-logo-primary.svg';
import BooksterSvg from '../../public/assets/img/integrations/bookster-logo-primary.svg';
import BoostSvg from '../../public/assets/img/integrations/boost-logo-primary.svg';
import CiirusSvg from '../../public/assets/img/integrations/ciirus-logo-primary.svg';
import EscapiaSvg from '../../public/assets/img/integrations/escapia-logo-primary.svg';
import GuestySvg from '../../public/assets/img/integrations/guesty-logo-primary.svg';
import HostAwaySvg from '../../public/assets/img/integrations/hostaway-logo-primary.svg';
import HostfullySvg from '../../public/assets/img/integrations/hostfully-logo-primary.svg';
import LiveRezSvg from '../../public/assets/img/integrations/live-rez-logo-primary.svg';
import LodgifySvg from '../../public/assets/img/integrations/lodgify-logo-primary.svg';
import LodgixSvg from '../../public/assets/img/integrations/lodgix-logo-primary.svg';
import OwnerRezSvg from '../../public/assets/img/integrations/ownerrez-logo-primary.svg';
import QuickConnectSvg from '../../public/assets/img/integrations/quick-connect-logo-primary.svg';
import RedAwningSvg from '../../public/assets/img/integrations/redawning-logo-primary.svg';
import RentalsUnitedSvg from '../../public/assets/img/integrations/rentals-united-logo-primary.svg';
import LmpmSvg from '../../public/assets/img/integrations/lmpm-logo-primary.svg';
import StreamlineSvg from '../../public/assets/img/integrations/streamline-logo-primary.svg';
import TrackSvg from '../../public/assets/img/integrations/track-logo-primary.svg';
import VacayHomeSvg from '../../public/assets/img/integrations/vacay-home-logo-primary.svg';
import VrmSvg from '../../public/assets/img/integrations/vrm-logo-primary.svg';

export interface Partner {
  id: string;
  title: string;
  value:
    | PmsName
    | 'QuickConnect'
    | 'BlueTent'
    | 'Lightmaker'
    | 'VRM'
    | 'Inhabit'
    | 'LiveRez'
    | 'LMPM';
  logo: StaticImageData;
  logoSvg: StaticImageData;
  subtext?: string;
  body?: string;
  helpRoute?: string;
  website?: string;
  comingSoon?: boolean;
  // Page-specific visibility
  showOnIntegrations: boolean;
  showOnList: boolean;
  showOnOnboarding: boolean;
}

export const PARTNERS: Partner[] = [
  {
    id: 'barefoot',
    title: 'Barefoot',
    value: PmsName.Barefoot,
    logo: Barefoot,
    logoSvg: BarefootSvg,
    subtext:
      'Enterprise trust accounting with owner portals and work order management.',
    body: 'A trusted name in vacation rental software, Barefoot delivers industrial-strength trust accounting and operations management. Advanced owner portals provide real-time financial visibility, while automated work orders streamline maintenance and housekeeping. Comprehensive financial controls, detailed owner statements, and integrated payment processing ensure accuracy and compliance at every step.',
    helpRoute: routes.barefootHelp,
    website: 'https://www.barefoot.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'blue-tent',
    title: 'Blue Tent',
    value: 'BlueTent',
    logo: BlueTent,
    logoSvg: BlueTentSvg,
    subtext:
      'Website optimization and digital marketing tools for direct booking growth.',
    body: "Blue Tent provides a comprehensive digital marketing and booking solution for vacation rental managers. The platform combines channel management with website optimization and digital marketing tools, complemented by a powerful booking engine integration. The analytics dashboard offers valuable insights for data-driven decision-making. Property managers can optimize their online presence to increase direct bookings while effortlessly connecting their marketing and booking systems. The platform's comprehensive analytics enable informed strategic decisions for business growth.",
    website: 'https://www.bluetent.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: false, // Wait until live
  },
  {
    id: 'boost',
    title: 'Boost',
    value: PmsName.Boost,
    logo: Boost,
    logoSvg: BoostSvg,
    subtext:
      'Dynamic pricing algorithms with automated distribution and unified inbox.',
    body: "Boost's innovative platform combines automated distribution with smart pricing engine capabilities, providing a comprehensive solution for vacation rental management. The system features a unified inbox for streamlined communication, detailed financial reporting, and extensive task automation. Property managers can enhance their operational efficiency while maximizing earnings through intelligent pricing and distribution strategies. The platform's single dashboard interface simplifies the management of all rental aspects, from booking to financial tracking.",
    helpRoute: routes.boostHelp,
    website: 'https://www.boosthotels.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'bookster',
    title: 'Bookster',
    value: PmsName.Bookster,
    logo: Bookster,
    logoSvg: BooksterSvg,
    subtext:
      'Automated guest screening and dynamic pricing with channel management.',
    body: "Bookster's rental management software combines channel synchronization with comprehensive booking tools. The platform offers automated guest screening, dynamic pricing, and integrated payment processing. Property managers can manage multiple channels, automate guest communication, and handle maintenance scheduling. The system includes owner reporting, financial management, and automated booking rules.",
    website: 'https://www.booksterhq.com/',
    showOnIntegrations: false,
    showOnList: false,
    showOnOnboarding: false,
  },
  {
    id: 'ciirus',
    title: 'Ciirus',
    value: PmsName.Ciirus,
    logo: Ciirus,
    logoSvg: CiirusSvg,
    subtext:
      'Channel management with automated guest communications and booking tools.',
    body: 'Ciirus provides integrated vacation rental software for property managers. The platform features channel management, automated guest communication, and comprehensive booking tools. Property managers can handle owner reporting, maintenance tracking, and financial management. The system includes real-time availability updates, payment processing, and automated guest communications.',
    helpRoute: routes.ciirusHelp,
    website: 'https://ciirus.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'escapia',
    title: 'Escapia',
    value: PmsName.Escapia,
    logo: Escapia,
    logoSvg: EscapiaSvg,
    subtext:
      'Enterprise vacation rental software with yield management and trust accounting.',
    body: "Escapia's enterprise vacation rental software delivers comprehensive property management solutions. The platform features advanced distribution capabilities, trust accounting, and integrated payment processing. Property managers can leverage automated owner statements, work order management, and real-time reporting. The system includes yield management tools, guest relationship management, and automated channel updates.",
    helpRoute: routes.escapiaHelp,
    website: 'https://escapia.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'guesty',
    title: 'Guesty',
    value: PmsName.GuestyDh,
    logo: Guesty,
    logoSvg: GuestySvg,
    subtext:
      'Unified dashboard for automation, channel management, and guest communications.',
    body: 'A global leader in short-term rental operations, Guesty revolutionizes property management with end-to-end automation. From multi-channel distribution to dynamic pricing algorithms, every aspect of property management becomes effortless. Customizable workflows, mobile-first tools, and a unified calendar empower managers to handle operations on the go. Built-in financial reporting and automated guest communication ensure nothing falls through the cracks.',
    helpRoute: routes.guestyHelp,
    website: 'https://www.guesty.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'hospitable',
    title: 'Hospitable',
    value: PmsName.Hospitable,
    logo: Hospitable,
    logoSvg: Hospitable,
    subtext:
      'Property management with direct booking. Guests pay on Hospitable.',
    body: 'Hospitable manages short-term rentals and takes the booking on its own checkout. Savvy imports the property, calendar, and rates, and sends the guest to Hospitable to pay.',
    website: 'https://www.hospitable.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'hostaway',
    title: 'HostAway',
    value: PmsName.Hostaway,
    logo: HostAway,
    logoSvg: HostAwaySvg,
    subtext:
      'Unified inbox for channel management with automated messaging and dynamic pricing tools.',
    body: 'A leading vacation rental management solution, Hostaway delivers comprehensive control through a unified inbox, dynamic pricing, and automated workflows. Connect seamlessly with Airbnb, Booking.com, and Vrbo while maintaining real-time calendar synchronization across all channels. Property managers gain access to detailed analytics, automated guest messaging, and customizable reporting to drive business growth.',
    helpRoute: routes.hostawayHelp,
    website: 'https://www.hostaway.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'hostfully',
    title: 'Hostfully',
    value: PmsName.Hostfully,
    logo: Hostfully,
    logoSvg: HostfullySvg,
    subtext:
      'Digital guidebook creation with integrated operations and maintenance tools.',
    body: 'Serving thousands of property managers worldwide, Hostfully stands out with its innovative digital guidebook creation alongside robust management tools. Branded guest guides enhance the rental experience, while automated messaging and mobile-first operations keep everything running smoothly. From cleaning schedules to maintenance tracking, every operational detail is managed through an intuitive dashboard, complemented by secure payment processing and guest communication tools.',
    helpRoute: routes.hostfullyHelp,
    website: 'https://www.hostfully.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'live-rez',
    title: 'LiveRez',
    value: 'LiveRez',
    logo: LiveRez,
    logoSvg: LiveRezSvg,
    subtext:
      'Property management software with CRM and trust accounting features.',
    body: "LiveRez's professional platform combines property management with channel distribution and CRM capabilities. The system features owner portals and trust accounting tools for comprehensive business management. Property managers can access enterprise-grade features while maintaining detailed reporting and owner portals. The platform ensures accurate accounting and owner payments while providing complete control over all operational aspects.",
    website: 'https://www.liverez.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: false, // Wait until live
  },

  {
    id: 'lmpm',
    title: 'LMPM',
    value: 'LMPM',
    logo: Lmpm,
    logoSvg: LmpmSvg,
    subtext:
      'Property management system with integrated operations and guest communications.',
    body: 'LMPM provides comprehensive property management solutions for vacation rental operators. The platform features integrated guest communication, maintenance tracking, and channel management capabilities. Property managers can streamline operations while maintaining detailed reporting and automated workflows.',
    website: 'https://www.lmpm.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: false, // Wait until live
  },
  {
    id: 'lodgify',
    title: 'Lodgify',
    value: PmsName.Lodgify,
    logo: Lodgify,
    logoSvg: LodgifySvg,
    subtext:
      'Website builder with integrated booking engine and channel management tools.',
    body: 'A leading website creation and booking management solution, Lodgify empowers vacation rental owners to build their direct booking business. Reduce OTA dependency with professional website templates and automated reservation management. Integrated payment processing and smart pricing tools maximize revenue potential. Multi-language support and mobile optimization ensure global reach and seamless guest experience.',
    helpRoute: routes.lodgifyHelp,
    website: 'https://www.lodgify.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'lodgix',
    title: 'Lodgix',
    value: PmsName.Lodgix,
    logo: Lodgix,
    logoSvg: LodgixSvg,
    subtext:
      'Automated scheduling system with housekeeping and maintenance coordination.',
    body: "Lodgix's property management platform specializes in automated scheduling and channel management. The system offers integrated payment processing, owner statements, and housekeeping management. Property managers can automate guest communication, manage maintenance schedules, and handle channel distribution. The platform includes dynamic pricing tools, booking rules automation, and comprehensive financial reporting.",
    website: 'https://www.lodgix.com/',
    showOnIntegrations: false,
    showOnList: false,
    showOnOnboarding: false,
  },
  {
    id: 'ownerrez',
    title: 'OwnerRez',
    value: PmsName.OwnerRez,
    logo: OwnerRez,
    logoSvg: OwnerRezSvg,
    subtext:
      'Direct booking engine with custom quote generation and channel sync.',
    body: 'OwnerRez specializes in direct booking solutions for vacation rental managers. The platform offers website integration, channel management, and automated messaging capabilities. Property managers can create custom quotes, manage rates dynamically, and process payments securely. The system includes automated guest communication, booking rules enforcement, and comprehensive channel synchronization.',
    helpRoute: routes.ownerRezHelp,
    website: 'https://www.ownerrez.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'quick-connect',
    title: 'QuickConnect',
    value: 'QuickConnect',
    logo: QuickConnect,
    logoSvg: QuickConnectSvg,
    subtext:
      'Channel management with automated rate sync and content distribution.',
    body: 'QuickConnect provides streamlined channel management solutions for vacation rentals. The platform features automated synchronization of rates, availability, and content across channels. Property managers can efficiently manage distribution while maintaining consistent property information. The system includes automated updates, booking management, and channel optimization tools.',
    helpRoute: routes.quickConnectHelp,
    website: 'https://www.quickconnect.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },

  {
    id: 'red-awning',
    title: 'Red Awning',
    value: PmsName.RedAwning,
    logo: RedAwning,
    logoSvg: RedAwningSvg,
    subtext:
      'Distribution across 50+ channels with 24/7 guest support and automated management.',
    body: 'RedAwning offers the largest marketing channel distribution for vacation rental owners and property managers, connecting properties with travelers across 50+ booking sites. The platform provides streamlined communication, centralized property management, dynamic door codes, and 24/7 guest support. Property managers can manage all properties within one interface while accessing automated guest messaging, cleaner coordination, and comprehensive booking management tools.',
    website: 'https://www.redawning.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: false,
  },
  {
    id: 'rentals-united',
    title: 'Rentals United',
    value: PmsName.RentalsUnited,
    logo: RentalsUnited,
    logoSvg: RentalsUnitedSvg,
    subtext:
      'Global channel management and distribution platform for vacation rentals.',
    body: 'Rentals United provides comprehensive channel management and distribution solutions for vacation rental managers. The platform offers global reach through extensive channel partnerships, automated content syndication, and intelligent rate management. Property managers can expand their market presence while maintaining consistent property information across all major booking channels.',
    website: 'https://www.rentalsunited.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: false, // Only on integrations and list pages
  },
  {
    id: 'streamline',
    title: 'Streamline',
    value: PmsName.Streamline,
    logo: Streamline,
    logoSvg: StreamlineSvg,
    subtext:
      'Enterprise property management with owner portals and trust accounting.',
    body: 'An enterprise leader in vacation rental management, Streamline delivers professional-grade solutions with military-grade security protocols. Built-in trust accounting ensures financial accuracy, while owner portals provide 24/7 access to statements and reports. Advanced work order management, automated distribution to major booking channels, and integrated CRM tools create a complete operational ecosystem. Housekeeping and maintenance modules coordinate staff efficiently, maximizing property uptime and guest satisfaction.',
    helpRoute: routes.streamlineHelp,
    website: 'https://www.streamlinevrs.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'track',
    title: 'Track',
    value: PmsName.Track,
    logo: Track,
    logoSvg: TrackSvg,
    subtext:
      'Revenue management system with dynamic pricing and automated reporting.',
    body: 'Used by leading property managers across North America, Track focuses on revenue optimization and operational excellence. Dynamic pricing algorithms analyze market data to maximize booking rates, while automated owner reporting keeps stakeholders informed. Advanced rate management tools, integrated guest communications, and comprehensive housekeeping coordination work together to boost efficiency and profitability.',
    helpRoute: routes.trackHelp,
    website: 'https://www.trackhs.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'vacay-home',
    title: 'VacayHome Connect',
    value: PmsName.VacayHome,
    logo: VacayHome,
    logoSvg: VacayHomeSvg,
    subtext:
      'Channel distribution platform with revenue optimization and content sync.',
    body: "VacayHome's distribution platform focuses on revenue optimization and channel management. The system offers automated content syndication, rate management, and booking synchronization. Property managers can expand their market reach while maintaining consistent property information across channels. The platform includes revenue optimization tools, automated updates, and comprehensive distribution features.",
    website: 'https://www.vacayhomeconnect.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: true,
  },
  {
    id: 'vrm',
    title: 'VRM',
    value: 'VRM',
    logo: Vrm,
    logoSvg: VrmSvg,
    subtext:
      'Trust accounting software with integrated work orders and owner portals.',
    body: "VRM's property management platform provides comprehensive tools for vacation rental operations. The system features integrated trust accounting, owner portals, and automated work orders. Property managers can streamline maintenance tracking, automate guest communication, and manage channel distribution. The platform includes real-time reporting, housekeeping management, and secure payment processing.",
    website: 'https://www.vrm.com/',
    showOnIntegrations: true,
    showOnList: true,
    showOnOnboarding: false, // Wait until live
  },
];

// Helper functions to get partners for specific pages
export const getIntegrationsPagePartners = (): Partner[] => {
  return PARTNERS.filter((partner) => partner.showOnIntegrations);
};

export const getListPagePartners = (): Partner[] => {
  return PARTNERS.filter((partner) => partner.showOnList);
};

export const getOnboardingPartners = (): Partner[] => {
  return PARTNERS.filter((partner) => partner.showOnOnboarding);
};

export const getActivePartners = (): Partner[] => {
  return PARTNERS.filter(
    (partner) => !partner.comingSoon && partner.showOnIntegrations
  );
};

export const getComingSoonPartners = (): Partner[] => {
  return PARTNERS.filter(
    (partner) => partner.comingSoon && partner.showOnIntegrations
  );
};

export const getPartnerById = (id: string): Partner | undefined => {
  return PARTNERS.find((partner) => partner.id === id);
};

export const getSupportedPmsIntegrationsForOnboarding = (
  isTesting: boolean
): {
  title: string;
  value: PmsName | 'QuickConnect';
  source: StaticImageData;
}[] => {
  const partners = getOnboardingPartners();

  const integrations = partners.map((partner) => ({
    title: partner.title,
    value: partner.value as PmsName | 'QuickConnect',
    source: partner.logo,
  }));

  // Testing-only cells, shown with ?pms_testing=true.
  if (isTesting) {
    integrations.push({
      title: 'Demo',
      value: PmsName.Demo,
      source: Arrow,
    });
  }

  return integrations;
};

/**
 * Maps the verbose PMS dropdown values shown in onboarding step 1 (from
 * `pmsCompanies`) to the onboarding integration value used in step 2's grid.
 * Step 1 stores the dropdown label on the session as `pmsType`; step 2 uses
 * this to preselect the PMS the user picked. Only includes entries with a
 * supported integration on the onboarding grid.
 */
export const PMS_DROPDOWN_TO_INTEGRATION: Record<
  string,
  PmsName | 'QuickConnect'
> = {
  Barefoot: PmsName.Barefoot,
  Boost: PmsName.Boost,
  CiiRus: PmsName.Ciirus,
  Escapia: PmsName.Escapia,
  Guesty: PmsName.GuestyDh,
  Hospitable: PmsName.Hospitable,
  HostAway: PmsName.Hostaway,
  'Hostfully (Orbirental)': PmsName.Hostfully,
  Lodgify: PmsName.Lodgify,
  OwnerRez: PmsName.OwnerRez,
  'Quick Connect': 'QuickConnect',
  Streamline: PmsName.Streamline,
  'Track Hospitality Software': PmsName.Track,
  'VacayHome Connect': PmsName.VacayHome,
};
